// One register (RFIs, notices, variations, claims, NDAs) — list with filters; click a row to open the item.
// Claims and variations: see MoneyRegister.tsx (V0.4.3).
// RFIs and notices get the summary on top (tiles, Responsibility, by building). Rows are coloured: closed (grey),
// overdue (red edge), SWI to act (orange edge + tag).
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { fmtDate, fmtMoney } from '../lib/format';
import { isClosed, itemFields, needsReview, registerFor, statusLabel, STATUS, TYPE_LOOK, type Item } from '../lib/registers';
import { TypeTag } from '../ui/TypeTag';
import { Badge, Button, Card, Empty, PageHeader } from '../ui/kit';
import { orgShort, dueText, type Org } from '../ui/item/shared';
import { RegisterSummary, AGE_BUCKETS, daysWithHolder, inAge, isOverdue, isSwi, raisedOn, useSummaryPrefs, type SumFilter } from '../ui/RegisterSummary';
import { RecordDrawer } from '../ui/RecordDrawer';
import { NewItemWindow } from '../ui/newitem/NewItemWindow';
import MoneyRegister from './MoneyRegister';
import { C3PageLoading } from '../ui/C3Loader';

const NONE = '00000000-0000-0000-0000-000000000000';
const today = () => new Date().toISOString().slice(0, 10);
type Row = Pick<Item, 'id' | 'number' | 'ref_no' | 'subtype' | 'title' | 'status' | 'building_ids' | 'buildings_raw' | 'org_id' | 'org_raw' | 'submitted_on' | 'respond_by' | 'amount' | 'ss_created_at' | 'created_at' | 'updated_at' | 'deleted_at' | 'onus_org_id' | 'onus_since' | 'review' | 'type'>;
/** registers that show the summary on top */
const WITH_SUMMARY = new Set(['rfi', 'notice']);
const QUICK: Record<string, string> = { open: 'Open', overdue: 'Overdue', swi: 'SWI to act', recent: 'Raised · 30 d' };

// "latest" = date submitted, else when it was first entered (Smartsheet or PM C3)
const when = (r: Row) => raisedOn(r);
const numOf = (r: Row) => Number(/(\d+)\s*$/.exec(r.number ?? '')?.[1] ?? -1);
const SORTS: { key: string; label: string; cmp: (a: Row, b: Row) => number; needs?: 'respond' | 'money' }[] = [
  { key: 'latest', label: 'Latest first', cmp: (a, b) => when(b).localeCompare(when(a)) || numOf(b) - numOf(a) },
  { key: 'oldest', label: 'Oldest first', cmp: (a, b) => when(a).localeCompare(when(b)) || numOf(a) - numOf(b) },
  { key: 'num_desc', label: 'Number — highest first', cmp: (a, b) => numOf(b) - numOf(a) },
  { key: 'num_asc', label: 'Number — lowest first', cmp: (a, b) => numOf(a) - numOf(b) },
  { key: 'topic_az', label: 'Topic A–Z', cmp: (a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) },
  { key: 'topic_za', label: 'Topic Z–A', cmp: (a, b) => b.title.localeCompare(a.title, 'en', { sensitivity: 'base' }) },
  { key: 'status', label: 'Status', cmp: (a, b) => statusLabel(a.status, a.type).localeCompare(statusLabel(b.status, b.type)) || when(b).localeCompare(when(a)) },
  { key: 'respond', label: 'Respond by — soonest first', needs: 'respond', cmp: (a, b) => (a.respond_by ?? '9999').localeCompare(b.respond_by ?? '9999') },
  { key: 'amount', label: 'Amount — highest first', needs: 'money', cmp: (a, b) => Number(b.amount ?? 0) - Number(a.amount ?? 0) },
];

/** Claims and variations have their own list (approval progress + money); the other registers use ListRegister. */
export default function Register() {
  const { module } = useParams();
  const def = registerFor(module);
  if (def && (def.type === 'claim' || def.type === 'variation')) return <MoneyRegister key={module} />;
  return <ListRegister key={module} />;
}

function ListRegister() {
  const { module } = useParams();
  const def = registerFor(module);
  const { siteId, site, can, session } = useApp();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  // V0.4.7: RFIs, notices and NDAs get the large new-item window (drop a file, Claude fills it in)
  const [newFiles, setNewFiles] = useState<File[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const bigNew = def ? ['rfi', 'notice', 'nda'].includes(def.type) : false;
  // V0.4.9: "+ Answer with a new SI" from an SI page → /r/rfis?newsi=<that SI's id>
  const [preset, setPreset] = useState<{ subtype?: string; answers?: string[] } | undefined>(undefined);
  const newSi = params.get('newsi');
  useEffect(() => {
    if (!newSi || def?.type !== 'rfi') return;
    setPreset({ subtype: 'Architects SI', answers: [newSi] });
    setNewFiles([]);
    setParams((p) => {
      p.delete('newsi');
      return p;
    }, { replace: true });
  }, [newSi, def?.type]); // eslint-disable-line react-hooks/exhaustive-deps
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const sort = params.get('sort') ?? 'latest';
  const building = params.get('b') ?? '';
  const review = params.get('review') === '1';
  const quick = params.get('f') ?? '';
  const onusF = params.get('onus') ?? '';
  const ageF = params.get('age') ?? '';
  const [prefs, setPrefs] = useSummaryPrefs(session?.user.id, module ?? '');
  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };

  const list = useRows<Row>(
    () =>
      supabase
        .from('items')
        .select('id, type, number, ref_no, subtype, title, status, building_ids, buildings_raw, org_id, org_raw, submitted_on, respond_by, amount, onus_org_id, onus_since, review, ss_created_at, created_at, updated_at, deleted_at')
        .eq('site_id', siteId ?? NONE)
        .eq('type', def?.type ?? 'none')
        .is('deleted_at', null)
        .limit(5000),
    [siteId, def?.type],
    ['items'],
  );
  const buildings = useRows<{ id: string; name: string }>(
    () => supabase.from('buildings').select('id, name').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('sort').order('name'),
    [siteId],
    ['buildings'],
  );
  const orgs = useRows<Org>(
    () => supabase.from('organisations').select('id, name, short_code, aliases, kind').is('deleted_at', null).order('name'),
    [],
    ['organisations'],
  );
  const bName = useMemo(() => new Map(buildings.rows.map((b) => [b.id, b.name])), [buildings.rows]);
  const oName = useMemo(() => new Map(orgs.rows.map((o) => [o.id, o.name])), [orgs.rows]);
  const oShort = useMemo(() => new Map(orgs.rows.map((o) => [o.id, orgShort(o)])), [orgs.rows]);
  const swiId = useMemo(() => orgs.rows.find((o) => o.short_code === 'SWI')?.id, [orgs.rows]);
  const toReview = useMemo(() => list.rows.filter(needsReview).length, [list.rows]);

  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const cmp = (SORTS.find((x) => x.key === sort) ?? SORTS[0]).cmp;
    return list.rows.filter((r) => {
      if (status === 'open' && isClosed(r.status)) return false;
      if (status !== 'open' && status !== 'all' && r.status !== status) return false;
      if (building === 'none' ? r.building_ids.length > 0 : building && !r.building_ids.includes(building)) return false;
      if (review && !needsReview(r)) return false;
      if (quick === 'open' && isClosed(r.status)) return false;
      if (quick === 'overdue' && !isOverdue(r)) return false;
      if (quick === 'swi' && !isSwi(r, swiId)) return false;
      if (quick === 'recent' && raisedOn(r) < new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10)) return false;
      // Responsibility and age filters are about open items
      if ((onusF || ageF) && isClosed(r.status)) return false;
      if (onusF && (r.onus_org_id ?? 'none') !== onusF) return false;
      if (ageF && !inAge(r, ageF)) return false;
      if (!words.length) return true;
      const hay = [r.number, r.ref_no, r.subtype, r.title, ...r.buildings_raw, r.org_raw].join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    }).sort(cmp);
  }, [list.rows, q, status, building, sort, review, quick, onusF, ageF, swiId]);

  if (!def) return <Navigate to="/" replace />;
  if (!can(def.module)) return <Navigate to="/" replace />;
  const editable = can(def.module, 4);
  const total = def.money ? shown.reduce((s, r) => s + Number(r.amount ?? 0), 0) : 0;
  const t = today();

  const summary = WITH_SUMMARY.has(def.type);
  const sumFilter: SumFilter = { f: quick, onus: onusF, age: ageF, b: building };
  const setSum = (k: keyof SumFilter, v: string) => setParam(k, v);
  const chips: { label: string; clear: () => void }[] = [];
  if (quick) chips.push({ label: QUICK[quick] ?? quick, clear: () => setParam('f', '') });
  if (onusF) chips.push({ label: `Responsibility: ${onusF === 'none' ? 'not set' : oShort.get(onusF) ?? '?'}`, clear: () => setParam('onus', '') });
  if (ageF) chips.push({ label: `${AGE_BUCKETS.find((a) => a.key === ageF)?.label ?? ageF} days with holder`, clear: () => setParam('age', '') });
  if (building) chips.push({ label: building === 'none' ? 'No building' : bName.get(building) ?? 'Building', clear: () => setParam('b', '') });
  const open = (id: string) => nav(`/r/${def.module}/${id}`);
  const lateN = list.rows.filter((r) => isOverdue(r, t)).length;
  const swiN = list.rows.filter((r) => isSwi(r, swiId)).length;
  const openN = list.rows.filter((r) => !isClosed(r.status)).length;

  return (
    <>
      <div className="type-band" style={{ background: TYPE_LOOK[def.type].color }} />
      <div
        className={`register-page ${dragging ? 'ni-dragging' : ''}`}
        onDragOver={(e) => {
          if (!bigNew || !editable || newFiles || !e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
        onDrop={(e) => {
          if (!bigNew || !editable || newFiles) return;
          e.preventDefault();
          setDragging(false);
          const fs = Array.from(e.dataTransfer.files);
          if (fs.length) setNewFiles(fs);
        }}
      >
        {dragging ? (
          <div className="ni-dropall" aria-hidden="true">
            <b>Drop to start a new {def.one}</b>
            <span>SWI AI reads each file — one {def.one} per file, you check each before it's saved</span>
          </div>
        ) : null}
      <PageHeader
        title={def.label}
        sub={
          <span className="item-head">
            <TypeTag type={def.type} /> {site?.name}
          </span>
        }
        actions={
          <>
            {summary ? (
              <Button kind="secondary" onClick={() => setPrefs({ hidden: !prefs.hidden })} aria-pressed={!prefs.hidden}>
                {prefs.hidden ? 'Show summary' : 'Hide summary'}
              </Button>
            ) : null}
            {editable ? <Button kind="primary" onClick={() => (bigNew ? setNewFiles([]) : setAdding(true))}>+ New {def.one}</Button> : null}
          </>
        }
      />
      {summary && !list.loading && list.rows.length ? (
        <RegisterSummary rows={list.rows} orgs={orgs.rows} buildings={buildings.rows} swiId={swiId} filter={sumFilter} setFilter={setSum} prefs={prefs} setPrefs={setPrefs} openItem={open} />
      ) : null}
      <div className="toolbar">
        <input type="search" placeholder="Find by number, topic, building…" value={q} onChange={(e) => setParam('q', e.target.value)} aria-label="Find" style={{ minWidth: 240 }} />
        <select value={status} onChange={(e) => setParam('status', e.target.value === 'all' ? '' : e.target.value)} aria-label="Status">
          <option value="all">All items</option>
          <option value="open">Open items</option>
          {def.statuses.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s, def.type)}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setParam('sort', e.target.value === 'latest' ? '' : e.target.value)} aria-label="Sort">
          {SORTS.filter((x) => (x.needs === 'respond' ? def.hasRespondBy : x.needs === 'money' ? Boolean(def.money) : true)).map((x) => (
            <option key={x.key} value={x.key}>
              {x.label}
            </option>
          ))}
        </select>
        <select value={building} onChange={(e) => setParam('b', e.target.value)} aria-label="Building">
          <option value="">All buildings</option>
          {buildings.rows.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
          <option value="none">No building</option>
        </select>
        {chips.map((c) => (
          <button key={c.label} type="button" className="chip-btn filter on" onClick={c.clear} aria-label={`Clear filter ${c.label}`}>
            {c.label} ✕
          </button>
        ))}
        {toReview || review ? (
          <button type="button" className={`chip-btn review ${review ? 'on' : ''}`} aria-pressed={review} onClick={() => setParam('review', review ? '' : '1')}>
            To be reviewed · {toReview}
          </button>
        ) : null}
        <span className="row-key" aria-label="Row colours">
          <span><i className="k-open" />Open</span>
          {def.hasRespondBy ? <span><i className="k-late" />Overdue</span> : null}
          {def.type !== 'nda' ? <span><i className="k-swi" />SWI to act</span> : null}
          <span><i className="k-closed" />Closed</span>
        </span>
      </div>
      <Card pad={false}>
        {list.error ? <div className="error-box">{list.error}</div> : null}
        {list.loading ? (
          <C3PageLoading label={`Loading ${def.label.toLowerCase()}`} />
        ) : shown.length === 0 ? (
          <Empty>{list.rows.length ? 'Nothing matches these filters.' : `No ${def.label.toLowerCase()} yet — import them from Smartsheet (Admin → Import) or add one.`}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t reg">
              <thead>
                <tr>
                  <th className="c-num">No.</th>
                  <th>{def.type === 'nda' ? 'Contractor / individual' : 'Topic'}</th>
                  <th className="c-status">Status</th>
                  {def.type !== 'nda' ? <th className="c-onus">Responsibility</th> : null}
                  {def.hasRespondBy ? <th className="c-date">Respond by</th> : def.type !== 'nda' ? <th className="c-date">Submitted</th> : null}
                  {def.money ? <th className="right c-amt">Amount {def.money}</th> : null}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const closed = isClosed(r.status);
                  const late = def.hasRespondBy && isOverdue(r, t);
                  const swi = def.type !== 'nda' && isSwi(r, swiId);
                  const names = r.building_ids.map((id) => bName.get(id)).filter(Boolean) as string[];
                  const where = names.length ? names.join(', ') : r.buildings_raw.join(', ');
                  const who = def.hasOrg ? (r.org_id && oName.get(r.org_id)) || r.org_raw : null;
                  const sub = [r.subtype, where, who].filter(Boolean).join(' · ');
                  const holder = r.onus_org_id && !closed ? oShort.get(r.onus_org_id) ?? '—' : null;
                  const held = holder ? daysWithHolder(r) : null;
                  const due = !closed && r.respond_by ? dueText(r.respond_by) : null;
                  const cls = ['click', closed ? 'row-closed' : '', late ? 'row-late' : '', swi ? 'row-swi' : ''].filter(Boolean).join(' ');
                  return (
                    <tr key={r.id} className={cls} onClick={() => open(r.id)}>
                      <td className="c-num">
                        <a className="num" href={`/r/${def.module}/${r.id}`} onClick={(e) => (e.preventDefault(), open(r.id))}>
                          {r.number ?? '—'}
                        </a>
                        {r.ref_no ? <div className="muted small">{r.ref_no}</div> : null}
                        {swi ? <div><span className="swi-tag">SWI to act</span></div> : null}
                        {needsReview(r) ? <span className="review-dot" title={`To be reviewed: ${r.review.length} thing(s) didn't match`}>To review</span> : null}
                      </td>
                      <td>
                        <div className="clip">{r.title}</div>
                        {sub ? <div className="sub-line">{sub}</div> : null}
                        <div className="m-only sub-line">
                          {statusLabel(r.status, def.type)}
                          {holder ? ` · with ${holder}` : ''}
                        </div>
                      </td>
                      <td className="c-status">
                        <Badge tone={closed ? (STATUS[r.status]?.tone === 'good' ? 'good' : 'neutral') : STATUS[r.status]?.tone ?? 'neutral'}>{statusLabel(r.status, def.type)}</Badge>
                      </td>
                      {def.type !== 'nda' ? (
                        <td className="c-onus">
                          {holder ? (
                            <>
                              {swi ? <b>{holder}</b> : holder}
                              <div className="sub-line">{held === 1 ? '1 day' : `${held} days`}</div>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                      ) : null}
                      {def.hasRespondBy ? (
                        <td className="c-date">
                          {late ? (
                            <span className="late-txt">
                              <span className="pdot" aria-hidden="true" />
                              {fmtDate(r.respond_by)}
                            </span>
                          ) : (
                            <span className="nowrap">{fmtDate(r.respond_by)}</span>
                          )}
                          {due ? (
                            <div className={`sub-line ${due.tone === 'late' ? 'overdue' : due.tone === 'soon' ? 'soon' : ''}`}>{due.text}</div>
                          ) : r.submitted_on ? (
                            <div className="sub-line">sent {fmtDate(r.submitted_on)}</div>
                          ) : null}
                        </td>
                      ) : def.type !== 'nda' ? (
                        <td className="c-date nowrap">{fmtDate(r.submitted_on)}</td>
                      ) : null}
                      {def.money ? <td className="right c-amt">{fmtMoney(r.amount)}</td> : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!list.loading && list.rows.length ? (
          <div className="count-line">
            Showing {shown.length} of {list.rows.length}
            {def.type !== 'nda' ? ` · ${openN} open` : ''}
            {def.hasRespondBy && lateN ? ` · ${lateN} overdue` : ''}
            {def.type !== 'nda' && swiN ? ` · ${swiN} SWI to act` : ''}
            {def.money ? ` · total ${fmtMoney(total)} ${def.money}` : ''}
          </div>
        ) : null}
      </Card>
      </div>

      {newFiles && siteId && def ? (
        <NewItemWindow
          def={def}
          siteId={siteId}
          files={newFiles}
          preset={preset}
          onClose={() => (setNewFiles(null), setPreset(undefined))}
          onSaved={(id, more) => {
            void list.reload();
            if (!more) {
              setNewFiles(null);
              setPreset(undefined);
              nav(`/r/${def.module}/${id}`);
            }
          }}
        />
      ) : null}
      <RecordDrawer
        open={adding}
        onClose={() => setAdding(false)}
        title={`New ${def.one}`}
        table="items"
        fields={itemFields(def, buildings.rows, orgs.rows)}
        record={null}
        defaults={{ site_id: siteId, type: def.type, status: def.statuses[0], amount_basis: def.money ?? null }}
        canEdit={editable}
        onSaved={(row) => row?.id && nav(`/r/${def.module}/${row.id}`)}
        label={(r) => `${def.one} ${String(r.title ?? '').slice(0, 60)}`}
      />
    </>
  );
}
